import * as React from 'react';
import {faCheckCircle} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import ImpressionTracker from '../../analytics/impression-tracker/ImpressionTracker';
import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {IDPVariant, Item} from '../item.class';
import {OrdersService} from '../../../client/orders/orders.service';
import {useService} from '../../react/ServiceContext';
import {Select, SelectOption} from '../../ui/forms/Select/Select';
import Button from '../../ui/Buttons/Button';

interface ProductFamilySelectProps {
    item: Item;
    variant: IDPVariant;
}

export const ProductFamilySelect = ({item, variant}: ProductFamilySelectProps) => {
    const componentName = `ProductFamilySelect`;
    const ordersService: OrdersService = useService(`ordersService`);

    const variantOptions = () => {
        const options: SelectOption[] = [];
        variant.children.forEach((childItem) =>
            options.push({
                key: childItem.childID,
                value: childItem.dropdownValue,
            }),
        );
        return options;
    };

    /**
     * Template
     */
    return (
        <form>
            <h6>
                {variant.name} : {variant.productValue}
            </h6>
            <div className="tw-mb-5">
                {variant.renderType?.toLowerCase() === `dropdown` && (
                    <Select
                        aria-label={variant.name}
                        className="[&_select]:!tw-h-[38px] [&_select]:!tw-pt-1.5"
                        defaultValue={item.id}
                        id="productFamilySelect__selectListDropdown"
                        label=""
                        name="iDPId"
                        onChange={(event) => {
                            const childItem = variant.children[event.target.selectedIndex];
                            gaLegacyCustomEvent({
                                eventAction: `product_family_dropdown`,
                                eventCategory: `IDP`,
                                eventLabel: `${childItem.childID}: ${childItem.dropdownValue} | ${
                                    item.termSpec?.noun_of_item_spec || item.webcatid
                                }`,
                            });
                            location.assign(`/item/${childItem.childID}`);
                        }}
                        options={variantOptions()}
                    />
                )}
                {variant.renderType?.toLowerCase() === `tile` && (
                    <>
                        {variant.children.map((childItem, index) => (
                            <ImpressionTracker
                                as="span"
                                data={{
                                    dimension16: ordersService?.currentOrderNumber,
                                    index,
                                    item_id: childItem.childID,
                                    item_list_name: componentName,
                                }}
                                key={index}
                            >
                                <Button
                                    className={`tw-mb-2 tw-mr-2 !tw-text-[16px] lg:!tw-text-[20px] !tw-rounded-none${
                                        childItem.childID === item.id ? ' tw-border-[3px]' : ''
                                    }`}
                                    diagonal={childItem.outOfStock}
                                    disabled={childItem.outOfStock}
                                    onClick={() => {
                                        gaLegacyCustomEvent({
                                            eventAction: `product_family_tiles`,
                                            eventCategory: `IDP`,
                                            eventLabel: `${childItem.childID}: ${childItem.dropdownValue} | ${
                                                item.termSpec?.noun_of_item_spec || item.webcatid
                                            }`,
                                        });
                                        location.assign(`/item/${childItem.childID}`);
                                    }}
                                    size="lg"
                                    variant="outline-secondary"
                                >
                                    {childItem.dropdownValue}
                                </Button>
                            </ImpressionTracker>
                        ))}
                    </>
                )}
                {variant.renderType?.toLowerCase().startsWith(`visual`) && (
                    <>
                        {variant.children.map((childItem, index) => {
                            if (childItem.dropdownAsset?.image) {
                                const isCircle = variant.renderType.toLowerCase() === `visual circle`;
                                return (
                                    <ImpressionTracker
                                        as="span"
                                        data={{
                                            dimension16: ordersService?.currentOrderNumber,
                                            index,
                                            item_id: childItem.childID,
                                            item_list_name: componentName,
                                        }}
                                        key={index}
                                    >
                                        <a
                                            href={`/item/${childItem.childID}`}
                                            onClick={() => {
                                                gaLegacyCustomEvent({
                                                    eventAction: `product_family_${isCircle ? 'circle' : 'square'}`,
                                                    eventCategory: `IDP`,
                                                    eventLabel: `${childItem.childID}: ${childItem.dropdownValue} | ${
                                                        item.termSpec?.noun_of_item_spec || item.webcatid
                                                    }`,
                                                });
                                            }}
                                        >
                                            <div
                                                className={`tw-h-[52px] tw-w-[52px] tw-mr-2 tw-mb-2 tw-inline-block tw-relative${
                                                    childItem.childID === item.id ? ' tw-border-[3px] tw-border-black' : ''
                                                }${isCircle ? ' tw-rounded-[50%] tw-p-1.5' : ''}${
                                                    childItem.outOfStock ? ' unavailable' : ''
                                                }`}
                                            >
                                                <img
                                                    alt={childItem.dropdownValue}
                                                    className={`${isCircle ? 'tw-rounded-[50%]' : ''} tw-max-w-full tw-h-auto`}
                                                    height="38"
                                                    src={`/${childItem.dropdownAsset.image}`}
                                                    width="38"
                                                />
                                                {isCircle && childItem.childID === item.id && (
                                                    <FontAwesomeIcon
                                                        className="tw-absolute tw-left-[35px] tw-top-[-5px] tw-bg-white tw-z-[1] [clip-path:circle(8px_at_center)]"
                                                        icon={faCheckCircle}
                                                    />
                                                )}
                                            </div>
                                        </a>
                                    </ImpressionTracker>
                                );
                            }
                        })}
                    </>
                )}
            </div>
        </form>
    );
};
